import axios from "axios";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { LoadingStatusCode } from "../../../../lib/types/LoadingStatusCode";
import { catchApiError } from "../../../../lib/utils/catchApiError";
import { Payment } from "../../../../lib/types/Payment";
import {
  getFetchAgreementBillsRoute,
  getFetchAgreementServicesRoute,
  getFetchPaymentAdjustmentsRoute,
  getFetchPaymentRoute,
} from "./accountPaymentDetailsRoutes";
import { PageableSearchResult } from "../../../../lib/interfaces";
import { Adjustment } from "../../../../lib/types/Adjustment";
import { BillWithAgreement } from "../../../../lib/types/BillWithAgreement";
import { sortByToDate } from "../../../../lib/utils";
import { IProduct } from "../../../../lib/types";

interface InitialState {
  payment?: Payment;
  paymentLoading: LoadingStatusCode;
  paymentAdjustments?: Adjustment[];
  paymentAdjustmentsLoading: LoadingStatusCode;
  agreementBills?: BillWithAgreement[];
  agreementBillsLoading: LoadingStatusCode;
  agreementServices?: IProduct[];
  agreementServicesLoading: LoadingStatusCode;
}

const initialState: InitialState = {
  paymentLoading: LoadingStatusCode.idle,
  paymentAdjustmentsLoading: LoadingStatusCode.idle,
  agreementBillsLoading: LoadingStatusCode.idle,
  agreementServicesLoading: LoadingStatusCode.idle,
};

export const fetchPayment = createAsyncThunk<Payment, number | string>(
  "accountPaymentDetails/fetchPayment",
  async (paymentId) => {
    try {
      const response = axios.get<PageableSearchResult<Payment[]>>(getFetchPaymentRoute(paymentId));
      const { data } = await response;
      return data.content[0];
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка получения шаблонов согласования пользователя",
      });
    }
  }
);

export const fetchPaymentAdjustments = createAsyncThunk<
  Adjustment[],
  Parameters<typeof getFetchPaymentAdjustmentsRoute>[0]
>("accountPaymentDetails/fetchPaymentAdjustments", async (params) => {
  try {
    const response = axios.get(getFetchPaymentAdjustmentsRoute(params));
    const { data } = await response;
    return data;
  } catch (e) {
    return catchApiError({
      e,
      title: "Ошибка получения шаблонов согласования пользователя",
    });
  }
});

export const fetchAgreementBills = createAsyncThunk<
  BillWithAgreement[],
  Parameters<typeof getFetchAgreementBillsRoute>[0]
>("accountPaymentDetails/fetchAgreementBills", async (params) => {
  try {
    const response = axios.get<BillWithAgreement[]>(getFetchAgreementBillsRoute(params));
    const { data } = await response;
    return data;
  } catch (e) {
    return catchApiError({
      e,
      title: "Ошибка загрузки счетов",
    });
  }
});

export const fetchAgreementServices = createAsyncThunk<
  IProduct[],
  Parameters<typeof getFetchAgreementServicesRoute>[0]
>("accountPaymentDetails/fetchAgreementServices", async (params) => {
  try {
    const response = axios.get<IProduct[]>(getFetchAgreementServicesRoute(params));
    const { data } = await response;
    return data;
  } catch (e) {
    return catchApiError({
      e,
      title: "Ошибка загрузки продуктов",
    });
  }
});

const slice = createSlice({
  name: "accountPaymentDetails",
  initialState,
  reducers: {
    cleanPayment: (state) => {
      state.paymentLoading = LoadingStatusCode.idle;
      state.payment = undefined;
    },
    cleanPaymentAdjustments: (state) => {
      state.paymentAdjustmentsLoading = LoadingStatusCode.idle;
      state.paymentAdjustments = undefined;
    },
    cleanAgreementBills: (state) => {
      state.agreementBillsLoading = LoadingStatusCode.idle;
      state.agreementBills = undefined;
    },
    cleanAgreementServices: (state) => {
      state.agreementServicesLoading = LoadingStatusCode.idle;
      state.agreementServices = undefined;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchPayment.pending, (state) => {
        state.paymentLoading = LoadingStatusCode.pending;
      })
      .addCase(fetchPayment.rejected, (state) => {
        state.paymentLoading = LoadingStatusCode.rejected;
        state.payment = undefined;
      })
      .addCase(fetchPayment.fulfilled, (state, { payload }) => {
        state.paymentLoading = LoadingStatusCode.fulfilled;
        state.payment = payload;
      })
      .addCase(fetchPaymentAdjustments.pending, (state) => {
        state.paymentAdjustmentsLoading = LoadingStatusCode.pending;
      })
      .addCase(fetchPaymentAdjustments.rejected, (state) => {
        state.paymentAdjustmentsLoading = LoadingStatusCode.rejected;
        state.paymentAdjustments = undefined;
      })
      .addCase(fetchPaymentAdjustments.fulfilled, (state, { payload }) => {
        state.paymentAdjustmentsLoading = LoadingStatusCode.fulfilled;
        state.paymentAdjustments = payload;
      })
      .addCase(fetchAgreementBills.pending, (state) => {
        state.agreementBillsLoading = LoadingStatusCode.pending;
      })
      .addCase(fetchAgreementBills.rejected, (state) => {
        state.agreementBillsLoading = LoadingStatusCode.rejected;
        state.agreementBills = undefined;
      })
      .addCase(fetchAgreementBills.fulfilled, (state, { payload }) => {
        state.agreementBillsLoading = LoadingStatusCode.fulfilled;
        state.agreementBills = [...payload].sort(sortByToDate);
      })
      .addCase(fetchAgreementServices.pending, (state) => {
        state.agreementServicesLoading = LoadingStatusCode.pending;
      })
      .addCase(fetchAgreementServices.rejected, (state) => {
        state.agreementServicesLoading = LoadingStatusCode.rejected;
        state.agreementServices = undefined;
      })
      .addCase(fetchAgreementServices.fulfilled, (state, { payload }) => {
        state.agreementServicesLoading = LoadingStatusCode.fulfilled;
        state.agreementServices = [...payload].sort(sortByToDate);
      });
  },
});

export const {
  actions: accountPaymentDetailsActions,
  reducer: accountPaymentDetailsReducer,
} = slice;
